import React from 'react';

// Import Components
import CountdownSlim from 'components/partials/countdown-slim';

// Import Images
import { ReactComponent as TabLogoWsf } from "img/logos/tab-logo-wsf.svg"
import { ReactComponent as TabLogoWsfSpring } from "img/logos/tab-logo-wsf-spring.svg"
import { ReactComponent as TabLogoOktoberfest } from "img/logos/tab-logo-oktoberfest.svg"
import { ReactComponent as TabLogoHolidayMagic } from "img/logos/tab-logo-holiday-magic.svg"
import { ReactComponent as TabLogoTaste } from "img/logos/tab-logo-taste.svg"
import { ReactComponent as TabLogoFoundation } from "img/logos/tab-logo-foundation.svg"

// Import Luxon
import { DateTime } from 'luxon';

const CountdownSlimBuilder: React.FC<{ countdownData?: Queries.CountdownBlockFragment }> = ({ countdownData }) => {

    if (!countdownData) return null;

    // if there's no date, return null
    if (!countdownData.signatureEvent?.startDate || !countdownData.signatureEvent?.endDate) {
        return null;
    }

    let TabLogo: React.FC<React.SVGProps<SVGElement>> = TabLogoWsf;

    switch (countdownData.signatureEvent?.logo) {

        case 'tab-logo-wsf.svg':
            TabLogo = TabLogoWsf;
            break;

        case 'tab-logo-wsf-spring.svg':
            TabLogo = TabLogoWsfSpring;
            break;

        case 'tab-logo-oktoberfest.svg':
            TabLogo = TabLogoOktoberfest;
            break;

        case 'tab-logo-holiday-magic.svg':
            TabLogo = TabLogoHolidayMagic;
            break;

        case 'tab-logo-taste.svg':
            TabLogo = TabLogoTaste;
            break;

        case 'tab-logo-foundation.svg':
            TabLogo = TabLogoFoundation;
            break;

        default:
            TabLogo = TabLogoWsf;
            break;
    }

    const scheduleItems = countdownData.signatureEvent?.schedule
        ?.filter((scheduleItem) => scheduleItem !== null) ?? [];

    // sort the schedule items by date and startime
    scheduleItems.sort((a, b) => {
        if (a?.date && b?.date) {
            return new Date(a.date).getTime() - new Date(b.date).getTime();
        }
        return 0;
    });

    // get the first date and time from the schedule items
    const firstScheduleItem = scheduleItems[0];

    const to24HourTime = firstScheduleItem?.startTime
        ? DateTime.fromFormat(firstScheduleItem.startTime, "h:mm a").toFormat("HH:mm")
        : "00:00";

    const startDate = firstScheduleItem?.date
        ? DateTime.fromISO(`${firstScheduleItem.date}T${to24HourTime}`, { zone: 'America/Los_Angeles' }).toJSDate()
        : DateTime.fromISO(countdownData.signatureEvent?.startDate ?? '', { zone: 'America/Los_Angeles' }).toJSDate();

    let endDate = DateTime.fromISO(countdownData.signatureEvent.endDate, { zone: 'America/Los_Angeles' }).toJSDate();

    let tagline = "Join us " + formatDateString(startDate, endDate);

    if (countdownData.heading) {
        tagline = countdownData.heading;
    }

    return (
        <CountdownSlim
            logo={TabLogo}
            tagline={tagline}
            eventStartDate={startDate}
            eventEndDate={endDate}
        />
    );
}

const formatDateString = (startDate: Date, endDate: Date): string => {
    const options: Intl.DateTimeFormatOptions = { month: 'long', day: 'numeric', timeZone: 'America/Los_Angeles' };
    const shortOptions: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', timeZone: 'America/Los_Angeles' };

    if (startDate.getMonth() === endDate.getMonth() && startDate.getFullYear() === endDate.getFullYear()) {
        let startFormat = new Intl.DateTimeFormat('en-US', options).format(startDate);
        let endDay = endDate.getDate();
        // example: "July 1-4"
        return `${startFormat.split(' ')[0]} ${startFormat.split(' ')[1]}-${endDay}`;
    } else {
        let startFormat = new Intl.DateTimeFormat('en-US', shortOptions).format(startDate);
        let endFormat = new Intl.DateTimeFormat('en-US', shortOptions).format(endDate);
        // example: "Jul 1 - Aug 4"
        return `${startFormat} - ${endFormat}`;
    }
};

export default CountdownSlimBuilder;